import React, { useCallback, useMemo, useState } from "react";
import { View, Pressable } from "react-native";
import SettingsToggle from "../common/settings-toggle";
import { Clock, ShoppingBag, Truck } from "lucide-react-native";
import HoursSelector from "../schedule/hours-selector";
import { IPrepTimeSchedule, ISchedule } from "../../../../types/agent-settings";
import StyledText from "../../../common/ui/styled-text";
import { Input } from "../../../common/ui/input.native";
import { useSettingsData } from "../../../../contexts/SettingsDataContext";
import { validationErrorsDefaultPrepTime } from "../../../../utils/setting-helpers";
import { usePostHog } from "posthog-js/react";

const BLANK_SCHEDULE: IPrepTimeSchedule = {
  monday: [],
  tuesday: [],
  wednesday: [],
  thursday: [],
  friday: [],
  saturday: [],
  sunday: [],
};

const CustomPrepTimes: React.FC = () => {
  const {
    isTestCallActive,
    settings,
    handleCustomPrepTimeEnabled,
    handleCustomPrepTimeHours,
    validationErrors,
  } = useSettingsData();
  const posthog = usePostHog();

  const orderingConfig = settings.orderingConfig || null;
  const isDisabled =
    isTestCallActive || orderingConfig?.dynamicPrepTime === true;
  const customEnabled = settings.customPrepTimeEnabled;
  const isDeliveryEnabled = !!orderingConfig?.deliveryModuleConfig?.enabled;
  const isPickupEnabled =
    !!orderingConfig?.pickupModuleConfig?.enabled || !isDeliveryEnabled;
  const globalPickupDefault = settings.pickupPrepTime || "30";
  const globalDeliveryDefault = settings.deliveryPrepTime || "30";

  const schedule: IPrepTimeSchedule = useMemo(
    () => settings.customPrepTimeHours || BLANK_SCHEDULE,
    [settings.customPrepTimeHours]
  );

  const [isEditing, setIsEditing] = useState(false);

  const toggleCustomEnabled = useCallback(
    (val: boolean) => {
      posthog.capture("prep_time_custom_enabled", {
        enabled: val,
      });

      const isEmptySchedule = Object.values(settings.customPrepTimeHours).every(
        (day) => day.length === 0
      );
      if (val && isEmptySchedule) {
        setIsEditing(true);
      }
      handleCustomPrepTimeEnabled(val);
    },
    [handleCustomPrepTimeEnabled, settings.customPrepTimeHours]
  );

  return (
    <SettingsToggle
      title="Custom prep times"
      description="Override default prep times for specific days and time ranges."
      value={customEnabled}
      onValueChange={toggleCustomEnabled}
      icon={Clock}
      disabled={isDisabled}
    >
      {customEnabled && (
        <View className="mt-4 space-y-4">
          <Pressable
            onPress={() => setIsEditing((prev) => !prev)}
            className={`w-full mb-2 flex-row items-center justify-center px-3 py-2 rounded-md bg-gray-50 hover:bg-gray-100 border border-gray-300 
              ${isDisabled ? "opacity-50" : ""}`}
          >
            <StyledText className="font-semibold text-gray-900">
              {isEditing ? "Done" : "Edit Times"}
            </StyledText>
          </Pressable>
          <HoursSelector
            schedule={schedule as ISchedule}
            isEditing={isEditing}
            onUpdateSchedule={handleCustomPrepTimeHours as any}
            disabled={isTestCallActive}
            readonly={isDisabled}
            displayEmptyDays={false}
            validationError={validationErrors.customPrepTime}
            allowedScheduleBounds={settings.schedule}
            allowOverlappingSlots={!validationErrors.customPrepTime}
            closedText="Using default prep times"
            renderSlotExtras={({
              slot,
              updateSlotField,
              disabled,
              day,
              index,
              isEditing,
            }) => {
              const pickupSlotEnabled = isEditing && isPickupEnabled;
              const deliverySlotEnabled = isEditing && isDeliveryEnabled;

              const {
                pickupPrepTime: pickupSlotErrors,
                deliveryPrepTime: deliverySlotErrors,
              } = validationErrorsDefaultPrepTime({
                pickupPrepTime: slot.pickup ?? globalPickupDefault,
                deliveryPrepTime: slot.delivery ?? globalDeliveryDefault,
                pickupEnabled: pickupSlotEnabled,
                deliveryEnabled: deliverySlotEnabled,
              });

              return (
                <View className="flex-row items-center space-x-3 ml-1">
                  {isEditing ? (
                    <>
                      {isPickupEnabled && (
                        <View
                          className={`flex-row items-center space-x-1 ${
                            !isPickupEnabled ? "opacity-50" : ""
                          }`}
                          pointerEvents={isPickupEnabled ? "auto" : "none"}
                        >
                          <ShoppingBag size={14} color="#16a34a" />
                          <Input
                            value={
                              slot.pickup != null ? String(slot.pickup) : ""
                            }
                            onChangeText={(v) => {
                              const sanitized = v.replace(/[^0-9]/g, "");
                              updateSlotField(
                                "pickup",
                                sanitized === "" ? null : parseInt(sanitized)
                              );
                            }}
                            keyboardType="number-pad"
                            placeholder={
                              slot.pickup == null && globalPickupDefault
                                ? globalPickupDefault
                                : "0"
                            }
                            placeholderTextColor="#9ca3af"
                            maxLength={4}
                            editable={!disabled && isPickupEnabled}
                            className={`w-14 h-10 text-center border rounded-md px-1 text-xs border-gray-200 
                            ${pickupSlotErrors ? "border-red-500" : ""}
                            ${!isPickupEnabled ? "bg-gray-100" : ""}`}
                          />
                          <StyledText className="text-[10px] text-gray-500">
                            min
                          </StyledText>
                        </View>
                      )}
                      {isDeliveryEnabled && (
                        <View
                          className={`flex-row items-center space-x-1 ${
                            !isDeliveryEnabled ? "opacity-50" : ""
                          }`}
                          pointerEvents={isDeliveryEnabled ? "auto" : "none"}
                        >
                          <Truck size={14} color="#1d4ed8" />
                          <Input
                            value={
                              slot.delivery != null ? String(slot.delivery) : ""
                            }
                            onChangeText={(v) => {
                              const sanitized = v.replace(/[^0-9]/g, "");
                              updateSlotField(
                                "delivery",
                                sanitized === "" ? null : parseInt(sanitized)
                              );
                            }}
                            keyboardType="number-pad"
                            placeholder={
                              slot.delivery == null && globalDeliveryDefault
                                ? globalDeliveryDefault
                                : "0"
                            }
                            placeholderTextColor="#9ca3af"
                            maxLength={4}
                            editable={!disabled && isDeliveryEnabled}
                            className={`w-14 h-10 text-center border rounded-md px-1 text-xs border-gray-200 
                            ${deliverySlotErrors ? "border-red-500" : ""}
                            ${!isDeliveryEnabled ? "bg-gray-100" : ""}`}
                          />
                          <StyledText className="text-[10px] text-gray-500">
                            min
                          </StyledText>
                        </View>
                      )}
                    </>
                  ) : (
                    <>
                      {isPickupEnabled && (
                        <View className="flex-row items-center space-x-1 w-16">
                          {slot.pickup != null && (
                            <>
                              <ShoppingBag size={14} color="#16a34a" />
                              <StyledText className="text-[10px] text-gray-500">
                                {slot.pickup != null ? slot.pickup : "—"} min
                              </StyledText>
                            </>
                          )}
                        </View>
                      )}
                      {isDeliveryEnabled && (
                        <View className="flex-row items-center space-x-1 w-16">
                          {slot.delivery != null && (
                            <>
                              <Truck size={14} color="#1d4ed8" />
                              <StyledText className="text-[10px] text-gray-500">
                                {slot.delivery != null ? slot.delivery : "—"}{" "}
                                min
                              </StyledText>
                            </>
                          )}
                        </View>
                      )}
                    </>
                  )}
                </View>
              );
            }}
          />
        </View>
      )}
    </SettingsToggle>
  );
};

export default CustomPrepTimes;
